import db from "../db/database";
import {
	fetchFromBusinessCentral,
	sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { getCurrentEnvironment } from "../utils/businessCentralUtils";
import { getPendingSync, isOnline } from "./syncService";
import { getTPVById, getTPVByNo } from "./tpvService";
import { COUNTRY_CODES } from "../constants/countries";

// Función helper para buscar un TPV tanto por ID como por No
const findTPV = async (identifier) => {
	try {
		// Primero intentar buscar por ID (número)
		if (typeof identifier === "number" || !isNaN(identifier)) {
			try {
				return await getTPVById(Number(identifier));
			} catch (error) {
				// Si no se encuentra por ID, continuar con la búsqueda por No
			}
		}

		// Buscar por No de Business Central (string)
		return await getTPVByNo(identifier);
	} catch (error) {
		throw new Error(`No se encontró el TPV con identificador ${identifier}`);
	}
};

// Función para transformar una caja al formato esperado por Business Central
const transformCajaForBC = async (caja) => {
	// Verificar que si hay un TPV, tenga un No de Business Central
	if (caja.TPV) {
		const tpv = await findTPV(caja.TPV);
		if (!tpv) {
			throw new Error(`No se encontró el TPV con identificador ${caja.TPV}`);
		}
		if (!tpv.No) {
			throw new Error(
				`El TPV ${tpv.nombre} no está sincronizado con Business Central. Por favor, sincronícelo primero.`
			);
		}
	}

	// Transformar la caja al formato esperado por BC
	const transformedCaja = {
		No_: caja.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		"No.": caja.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		No: caja.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		Nombre: caja.nombre,
		TPV: caja.TPV ? (await findTPV(caja.TPV)).No : "",
		Deleted: caja.deleted,
	};

	// Crear el formato final esperado por el endpoint
	return {
		data: JSON.stringify({ Cajas: [transformedCaja] }),
	};
};

export const mapCajaFromBC = (apiObj, country) => {

	const base = {
		No: apiObj.No ?? "",
		nombre: apiObj.Nombre ?? "",
		TPV: apiObj.TPV ?? apiObj.Tienda ?? "",
		tpvNombre: apiObj.Nombre ?? "",
	};

	// Honduras → necesitamos datos fiscales
	if (country === COUNTRY_CODES.HONDURAS) {
		return {
			...base,
			Tienda: apiObj.Tienda ?? "",
			Numerador_facturas: apiObj.Numerador_facturas ?? "",
			Numerador_abonos: apiObj.Numerador_abonos ?? "",
			Numerador_facturas_registradas: apiObj.Numerador_facturas_registradas ?? "",
			Numerador_abonos_registrados: apiObj.Numerador_abonos_registrados ?? "",
			Numerador_facturas_NFC: apiObj.Numerador_facturas_NFC ?? "",
			Numerador_abonos_NFC: apiObj.Numerador_abonos_NFC ?? "",
		};
	}

	return base;
};

export const syncCajas = async () => {
	if (!isOnline()) {
		return false;
	}

	try {
		const env = await getCurrentEnvironment();
		const pendingCajas = await getPendingSync("cajas");

		// Enviar cajas pendientes una por una
		if (pendingCajas.length > 0) {
			console.log(`Sincronizando ${pendingCajas.length} cajas pendientes...`);

			for (const caja of pendingCajas) {
				try {
					// Si la caja está marcada como eliminada, la eliminamos directamente
					if (caja.deleted) {
						await db.cajas.delete(caja.id);
						continue;
					}

					// Enviar caja individual a Business Central
					const cajaData = await transformCajaForBC(caja);
					const response = await sendToBusinessCentral(
						"Importaciones_insertaCajas",
						cajaData,
						true
					);

					// Verificar respuesta y actualizar la caja con el No asignado por BC
					if (response && response.value) {
						const cajaNo = response.value;

						console.log(
							`Caja ${caja.nombre} (ID: ${caja.id}) sincronizada con No: ${cajaNo}`
						);

						// Actualizar la caja local con el No de BC
						await db.cajas.update(caja.id, {
							No: cajaNo,
							lastModified: new Date(),
							lastSynced: new Date(),
							pendingSync: 0,
						});
					} else {
						console.error(
							`Error al sincronizar caja ${caja.id}: Respuesta inválida de BC`
						);
					}
				} catch (error) {
					console.error(`Error al sincronizar caja ${caja.id}:`, error);
					// Notificar al usuario del error específico
					window.messageApi?.error(
						`Error al sincronizar caja ${caja.nombre}: ${error.message}`
					);
				}
			}
		}

		// Obtener cajas del servidor después de sincronizar las pendientes
		const endpoint = env.country === COUNTRY_CODES.HONDURAS ? "CajaTPV" : "Cajas";

		const serverCajas = await fetchFromBusinessCentral(endpoint);

		const localCajas = await db.cajas.toArray();

		// Mapa para buscar cajas locales por No rápidamente
		const localCajasByNo = {};
		localCajas.forEach((caja) => {
			if (caja.No) {
				localCajasByNo[caja.No] = caja;
			}
		});

		// Actualizar cajas existentes y añadir nuevas desde el servidor
		for (const serverCajaRaw of serverCajas) {
			const serverCaja = mapCajaFromBC(serverCajaRaw, env.country);
			const localCaja = localCajasByNo[serverCaja.No];

			if (localCaja) {
				if (localCaja.pendingSync === 0) {
					await db.cajas.update(localCaja.id, {
						...serverCaja,
						lastModified: new Date(),
						lastSynced: new Date(),
					});
				}
			} else {
				await db.cajas.add({
					...serverCaja,
					lastModified: new Date(),
					lastSynced: new Date(),
					pendingSync: 0,
					deleted: false,
				});
			}
		}

		return true;
	} catch (error) {
		console.error("Error al sincronizar cajas:", error);
		return false;
	}
};
